import { Badge, Button, IconButton } from '@artosera/ui';
import { formatDate, type Devise } from '@artosera/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { archiveFacturesApi, useFacturesAnterieures } from '../../api/archiveFactures.js';
import { ouvrirUrlSignee } from '../../api/client.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { capturerPortee, estActuelle } from '../../auth/identite.js';
import styles from './ArchiveFactures.module.css';

/** Les centimes d'origine restent entiers, y compris à la borne safeinteger T6. */
function montantOriginal(cents: number, devise: Devise, langue: 'fr' | 'en'): string {
  const entier = BigInt(cents);
  const centimes = (entier < 0n ? -entier : entier) % 100n;
  const unites = entier / 100n;
  const format = new Intl.NumberFormat(langue === 'fr' ? 'fr-FR' : 'en-US', {
    style: 'currency',
    currency: devise,
    minimumFractionDigits: centimes === 0n ? 0 : 2,
    maximumFractionDigits: centimes === 0n ? 0 : 2,
  });
  // -0 préserve le signe des montants négatifs inférieurs à une unité.
  return format
    .formatToParts(unites === 0n && entier < 0n ? -0 : unites)
    .map((partie) =>
      partie.type === 'fraction' ? centimes.toString().padStart(2, '0') : partie.value,
    )
    .join('');
}

/** Pièces émises avant Artosera : seule action, ouvrir le PDF original signé. */
export function FacturesAnterieures({
  galerieSlug,
}: {
  readonly galerieSlug: string;
}): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'ventes', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [page, setPage] = useState(1);
  const [erreur, setErreur] = useState<string | null>(null);
  const liste = useFacturesAnterieures(galerieSlug, page);
  const factures = liste.data?.factures ?? [];
  const pagination = liste.data?.pagination;

  async function ouvrirPdf(id: string): Promise<void> {
    const capture = capturerPortee();
    setErreur(null);
    try {
      const reponse = await dansLaPorteeCourante((appel) =>
        archiveFacturesApi.urlPdfAnterieure(galerieSlug, id, appel),
      );
      ouvrirUrlSignee(reponse.url);
    } catch (error) {
      if (!estActuelle(capture)) return;
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <section id="anterieures" className={styles.classeur} aria-labelledby="anterieures-titre">
      <h2 className={styles.classeurAnnee} id="anterieures-titre">
        {t('regie:archiveFactures.anterieures.titre')}
      </h2>
      <p className={styles.sousTitre}>{t('regie:archiveFactures.anterieures.sousTitre')}</p>
      {liste.isLoading ? (
        <p className={styles.vide}>{t('commun:chargement')}</p>
      ) : liste.isError ? (
        <p className={styles.vide} role="alert">
          {estApiError(liste.error) ? liste.error.message : t('commun:erreurReseau')}
        </p>
      ) : factures.length === 0 ? (
        <p className={styles.vide}>{t('regie:archiveFactures.anterieures.aucune')}</p>
      ) : (
        <div className={styles.tableauEnveloppe}>
          <table
            className={`${styles.tableau} ${styles.tableauHistorique}`}
            data-density="registre"
          >
            <thead>
              <tr>
                <th scope="col">{t('regie:archiveFactures.anterieures.colonneNumero')}</th>
                <th scope="col">{t('regie:ventes.colonneDate')}</th>
                <th scope="col">{t('regie:archiveFactures.anterieures.colonneClient')}</th>
                <th scope="col" className={styles.colMontant}>
                  {t('regie:ventes.colonneTtc')}
                </th>
                <th scope="col">{t('regie:archiveFactures.anterieures.colonneLogiciel')}</th>
                <th scope="col">{t('regie:ventes.colonneDocuments')}</th>
              </tr>
            </thead>
            <tbody>
              {factures.map((facture) => (
                <tr key={facture.id}>
                  <td className={styles.celNumero}>
                    <span className={styles.numero}>{facture.numeroOrigine}</span>
                    {facture.type === 'avoir' ? (
                      <Badge tone="neutral">{t('ventes:factures.avoir')}</Badge>
                    ) : null}
                  </td>
                  <td className={styles.celDate}>
                    {formatDate(new Date(facture.emiseLe), langue, 'court')}
                  </td>
                  <td className={styles.celContact}>{facture.clientNom}</td>
                  <td className={`${styles.colMontant} ${styles.celTtc}`}>
                    <span className={styles.montantHistorique} lang={langue}>
                      {montantOriginal(facture.totalTtcCentimes, facture.devise, langue)}
                    </span>
                  </td>
                  <td className={styles.celLogiciel}>
                    {facture.logicielOrigine === '' ? '—' : facture.logicielOrigine}
                  </td>
                  <td className={styles.celDocuments}>
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label={t('regie:archiveFactures.anterieures.ouvrirPdf', {
                        numero: facture.numeroOrigine,
                      })}
                      onClick={() => void ouvrirPdf(facture.id)}
                    >
                      {t('regie:archiveFactures.anterieures.pdf')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {erreur ? (
        <p className={styles.vide} role="alert">
          {erreur}
        </p>
      ) : null}
      {pagination && pagination.pages > 1 ? (
        <div className={styles.pagination}>
          <IconButton
            variant="outline"
            icon={<ChevronLeft aria-hidden />}
            aria-label={t('regie:ventes.pagination.precedent')}
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((valeur) => Math.max(1, valeur - 1))}
          />
          <span>
            {t('regie:ventes.pagination.page', { page: pagination.page, pages: pagination.pages })}
          </span>
          <IconButton
            variant="outline"
            icon={<ChevronRight aria-hidden />}
            aria-label={t('regie:ventes.pagination.suivant')}
            size="sm"
            disabled={page >= pagination.pages || liste.isPlaceholderData}
            onClick={() => setPage((valeur) => Math.min(pagination.pages, valeur + 1))}
          />
        </div>
      ) : null}
    </section>
  );
}
